<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import SelectInput from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);
const { t } = useI18n();

const state = reactive({
  messageBufferEnabled: false,
  messageBufferSeconds: 15,
  conversationPdfEnabled: false,
  responseFragmentationEnabled: false,
  followUpPlanningEnabled: false,
  inactivityAutomationEnabled: true,
  inactivityHours: 1,
  autoResolveMode: 'evaluated',
});
const attemptedSubmit = ref(false);

const autoResolveOptions = computed(() => [
  {
    value: 'evaluated',
    label: t(
      'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.AUTO_RESOLVE_MODE.OPTIONS.EVALUATED'
    ),
  },
  {
    value: 'legacy',
    label: t(
      'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.AUTO_RESOLVE_MODE.OPTIONS.LEGACY'
    ),
  },
  {
    value: 'disabled',
    label: t(
      'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.AUTO_RESOLVE_MODE.OPTIONS.DISABLED'
    ),
  },
]);

const bufferSecondsValid = computed(
  () =>
    Number.isInteger(state.messageBufferSeconds) &&
    state.messageBufferSeconds >= 1 &&
    state.messageBufferSeconds <= 60
);

const inactivityHoursValid = computed(
  () =>
    Number.isInteger(state.inactivityHours) &&
    state.inactivityHours >= 1 &&
    state.inactivityHours <= 168
);

const hydrate = assistant => {
  const config = assistant?.config || {};
  state.messageBufferEnabled = config.message_buffer_enabled ?? false;
  state.messageBufferSeconds = config.message_buffer_seconds ?? 15;
  state.conversationPdfEnabled = config.conversation_pdf_enabled ?? false;
  state.responseFragmentationEnabled =
    config.response_fragmentation_enabled ?? false;
  state.followUpPlanningEnabled = config.follow_up_planning_enabled ?? false;
  state.inactivityAutomationEnabled =
    config.pending_conversation_automation_enabled ?? true;
  state.inactivityHours = config.pending_conversation_inactivity_hours ?? 1;
  state.autoResolveMode = config.auto_resolve_mode ?? 'evaluated';
  attemptedSubmit.value = false;
};

const handleSubmit = () => {
  attemptedSubmit.value = true;
  if (
    !bufferSecondsValid.value ||
    (state.inactivityAutomationEnabled && !inactivityHoursValid.value)
  ) {
    return;
  }

  const config = {
    ...props.assistant.config,
    message_buffer_enabled: state.messageBufferEnabled,
    message_buffer_seconds: state.messageBufferSeconds,
    conversation_pdf_enabled: state.conversationPdfEnabled,
    response_fragmentation_enabled: state.responseFragmentationEnabled,
    follow_up_planning_enabled: state.followUpPlanningEnabled,
    pending_conversation_automation_enabled: state.inactivityAutomationEnabled,
    auto_resolve_mode: state.autoResolveMode,
  };

  if (state.inactivityAutomationEnabled) {
    config.pending_conversation_inactivity_hours = state.inactivityHours;
  }

  emit('submit', { config });
};

watch(() => props.assistant, hydrate, { immediate: true });
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="handleSubmit">
    <p
      class="rounded-lg bg-n-alpha-2 px-4 py-3 text-sm leading-5 text-n-slate-11"
    >
      {{
        t('CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.CHANNEL_NOTICE')
      }}
    </p>

    <div class="flex items-start justify-between gap-4">
      <div class="flex min-w-0 flex-col gap-1">
        <h3
          id="message-buffer-label"
          class="text-sm font-medium text-n-slate-12"
        >
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.MESSAGE_BUFFER.TITLE'
            )
          }}
        </h3>
        <p
          id="message-buffer-description"
          class="text-sm leading-5 text-n-slate-11"
        >
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.MESSAGE_BUFFER.DESCRIPTION'
            )
          }}
        </p>
      </div>
      <Switch
        v-model="state.messageBufferEnabled"
        data-test="message-buffer-switch"
        aria-labelledby="message-buffer-label"
        aria-describedby="message-buffer-description"
      />
    </div>

    <div
      v-if="state.messageBufferEnabled"
      class="flex flex-col gap-1 rounded-lg border border-n-weak px-4 py-3"
    >
      <label
        for="captain-message-buffer-seconds"
        class="text-sm font-medium text-n-slate-12"
      >
        {{
          t(
            'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.BUFFER_SECONDS.LABEL'
          )
        }}
      </label>
      <input
        id="captain-message-buffer-seconds"
        v-model.number="state.messageBufferSeconds"
        data-test="buffer-seconds"
        type="number"
        min="1"
        max="60"
        step="1"
        class="h-10 w-28 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus-visible:outline-n-brand"
        :aria-invalid="attemptedSubmit && !bufferSecondsValid"
        :aria-describedby="
          attemptedSubmit && !bufferSecondsValid
            ? 'captain-buffer-seconds-error'
            : 'captain-buffer-seconds-help'
        "
      />
      <p
        id="captain-buffer-seconds-help"
        class="text-sm leading-5 text-n-slate-11"
      >
        {{
          t(
            'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.BUFFER_SECONDS.HELP'
          )
        }}
      </p>
      <p
        v-if="attemptedSubmit && !bufferSecondsValid"
        id="captain-buffer-seconds-error"
        role="alert"
        class="text-sm text-n-ruby-9"
      >
        {{
          t(
            'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.BUFFER_SECONDS.ERROR'
          )
        }}
      </p>
    </div>

    <div
      class="flex items-start justify-between gap-4 border-t border-n-weak pt-5"
    >
      <div class="flex min-w-0 flex-col gap-1">
        <h3
          id="conversation-pdf-label"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.PDF.TITLE') }}
        </h3>
        <p
          id="conversation-pdf-description"
          class="text-sm leading-5 text-n-slate-11"
        >
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.PDF.DESCRIPTION'
            )
          }}
        </p>
      </div>
      <Switch
        v-model="state.conversationPdfEnabled"
        data-test="pdf-switch"
        aria-labelledby="conversation-pdf-label"
        aria-describedby="conversation-pdf-description"
      />
    </div>

    <div
      class="flex items-start justify-between gap-4 border-t border-n-weak pt-5"
    >
      <div class="flex min-w-0 flex-col gap-1">
        <h3
          id="fragmentation-label"
          class="text-sm font-medium text-n-slate-12"
        >
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.FRAGMENTATION.TITLE'
            )
          }}
        </h3>
        <p
          id="fragmentation-description"
          class="text-sm leading-5 text-n-slate-11"
        >
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.FRAGMENTATION.DESCRIPTION'
            )
          }}
        </p>
      </div>
      <Switch
        v-model="state.responseFragmentationEnabled"
        data-test="fragmentation-switch"
        aria-labelledby="fragmentation-label"
        aria-describedby="fragmentation-description"
      />
    </div>

    <div
      class="flex items-start justify-between gap-4 border-t border-n-weak pt-5"
    >
      <div class="flex min-w-0 flex-col gap-1">
        <h3 id="follow-up-label" class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.FOLLOW_UP.TITLE'
            )
          }}
        </h3>
        <p id="follow-up-description" class="text-sm leading-5 text-n-slate-11">
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.FOLLOW_UP.DESCRIPTION'
            )
          }}
        </p>
      </div>
      <Switch
        v-model="state.followUpPlanningEnabled"
        data-test="follow-up-planning"
        aria-labelledby="follow-up-label"
        aria-describedby="follow-up-description"
      />
    </div>

    <div class="flex flex-col gap-2 border-t border-n-weak pt-5">
      <label
        class="flex flex-col items-start gap-2 text-sm font-medium text-n-slate-12"
      >
        <span>
          {{
            t(
              'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.AUTO_RESOLVE_MODE.LABEL'
            )
          }}
        </span>
        <SelectInput
          v-model="state.autoResolveMode"
          data-test="auto-resolve-mode"
          :options="autoResolveOptions"
        />
      </label>
      <p class="text-sm leading-5 text-n-slate-11">
        {{
          t(
            'CAPTAIN.ASSISTANTS.SETTINGS.CONVERSATION_BEHAVIOR.AUTO_RESOLVE_MODE.DESCRIPTION'
          )
        }}
      </p>
    </div>

    <div class="flex flex-col gap-4 border-t border-n-weak pt-5">
      <div class="flex items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <h3
            id="pending-conversation-automation-label"
            class="text-sm font-medium text-n-slate-12"
          >
            {{ t('CAPTAIN.ASSISTANTS.SETTINGS.INACTIVITY_AUTOMATION.TITLE') }}
          </h3>
          <p
            id="pending-conversation-automation-description"
            class="text-sm leading-5 text-n-slate-11"
          >
            {{
              t('CAPTAIN.ASSISTANTS.SETTINGS.INACTIVITY_AUTOMATION.DESCRIPTION')
            }}
          </p>
        </div>
        <Switch
          v-model="state.inactivityAutomationEnabled"
          data-test="inactivity-automation-switch"
          aria-labelledby="pending-conversation-automation-label"
          aria-describedby="pending-conversation-automation-description"
        />
      </div>

      <div
        v-if="state.inactivityAutomationEnabled"
        class="flex flex-col gap-1 rounded-lg border border-n-weak px-4 py-3"
      >
        <label
          for="pending-conversation-inactivity-hours"
          class="text-sm font-medium text-n-slate-12"
        >
          {{
            t('CAPTAIN.ASSISTANTS.SETTINGS.INACTIVITY_AUTOMATION.HOURS.LABEL')
          }}
        </label>
        <input
          id="pending-conversation-inactivity-hours"
          v-model.number="state.inactivityHours"
          data-test="inactivity-hours"
          type="number"
          min="1"
          max="168"
          step="1"
          class="h-10 w-28 rounded-lg border-0 bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak focus-visible:outline-n-brand"
          :aria-invalid="attemptedSubmit && !inactivityHoursValid"
          :aria-describedby="
            attemptedSubmit && !inactivityHoursValid
              ? 'pending-conversation-inactivity-hours-error'
              : 'pending-conversation-inactivity-hours-help'
          "
        />
        <p
          id="pending-conversation-inactivity-hours-help"
          class="text-sm leading-5 text-n-slate-11"
        >
          {{
            t('CAPTAIN.ASSISTANTS.SETTINGS.INACTIVITY_AUTOMATION.HOURS.HELP')
          }}
        </p>
        <p
          v-if="attemptedSubmit && !inactivityHoursValid"
          id="pending-conversation-inactivity-hours-error"
          role="alert"
          class="text-sm text-n-ruby-9"
        >
          {{
            t('CAPTAIN.ASSISTANTS.SETTINGS.INACTIVITY_AUTOMATION.HOURS.ERROR')
          }}
        </p>
      </div>

      <p
        class="rounded-lg bg-n-alpha-2 px-4 py-3 text-sm leading-5 text-n-slate-11"
      >
        {{ t('CAPTAIN.ASSISTANTS.SETTINGS.INACTIVITY_AUTOMATION.NOTICE') }}
      </p>
    </div>

    <div>
      <Button
        data-test="save-behavior"
        type="submit"
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
      />
    </div>
  </form>
</template>
